/**
 * 点击通知 → 站内跳到会话（任务 18b，主线程侧）。
 *
 * SW 点击通知时优先 focus 已开着的窗口并 postMessage（见 sw/pushEvents.ts 文件头：
 * 不走 client.navigate，避免整页重载）。本模块就是那条消息的接收端：
 * 解析站内路径 → `openChat`，**零请求、零 UI**。
 *
 * 判定（哪个窗口、去哪个 URL）全在 `sw/pushEvents.ts` 并有单测，这里只是薄接线，
 * 所以不单测（同 sw/register.ts 的处理）；端到端由 e2e `push-notify.spec.ts` 覆盖：
 * 直接在页面上合成一个 `message` 事件，等价于 SW 投来的那条。
 */
import { parseRoute } from '~/nav/routes';
import { SW_OPEN_CHAT } from '~/sw/pushEvents';

/** 页面侧需要的最小 SW 容器形状（可注入，e2e 用真的 navigator.serviceWorker） */
export interface SwMessageSource {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
}

/** 返回取消订阅函数；不支持 SW 的环境返回 no-op */
export function connectNotificationClicks(
  openConversation: (conversationKey: string) => void,
  source: SwMessageSource | null = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? navigator.serviceWorker
    : null,
): () => void {
  if (!source) return () => undefined;
  const listener = (event: MessageEvent): void => {
    const data: unknown = event.data;
    if (typeof data !== 'object' || data === null) return;
    const payload = data as Record<string, unknown>;
    if (payload['type'] !== SW_OPEN_CHAT || typeof payload['url'] !== 'string') return;
    const route = parseRoute(payload['url']);
    if (route?.kind === 'chat') openConversation(route.conversationKey);
  };
  source.addEventListener('message', listener);
  return () => source.removeEventListener('message', listener);
}
